// src/pages/admin/tabs/NotificationsTab.jsx
// Admin UI to send push notifications with dynamic template-based payloads.
// Supports template CRUD, category selection, image URL, and placeholder substitution.

import { useState, useMemo } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { B, fonts, Spinner, Btn } from "../../../components/UI";
import { withCloudinaryNotificationOptimization } from "../../../utils/cloudinary";

const PRODUCTS_API_URL =
  process.env.REACT_APP_PRODUCTS_API_URL || "https://products.nilamsynthetics.com";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const substitutePlaceholders = (text, count, category) => {
  if (!text) return "";
  return text
    .replace(/\{count\}/g, count)
    .replace(/\{category\}/g, category || "")
    .replace(/\{category_lower\}/g, (category || "").toLowerCase())
    .replace(/\{plural\}/g, Number(count) > 1 ? "s" : "")
    .replace(/\{plural_count\}/g, Number(count) !== 1 ? "s" : "");
};

// ─── Template Form (for add / edit) ──────────────────────────────────────────
function TemplateForm({ initial, onSave, onCancel, saving }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [body, setBody] = useState(initial?.body || "");

  const handleSave = async () => {
    if (!title.trim()) return;
    await onSave({ title: title.trim(), body: body.trim() });
  };

  const inputStyle = {
    width: "100%", padding: "10px 14px", fontSize: 14, fontWeight: 500,
    fontFamily: fonts.body, border: `2px solid ${B.border}`,
    borderRadius: 8, outline: "none", background: B.cream, color: B.text,
    boxSizing: "border-box", transition: "border-color 0.2s",
  };

  return (
    <div style={{ background: B.orangeFaint, border: `1.5px solid ${B.orangeMid}`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: B.text, marginBottom: 16, fontFamily: fonts.adminDisplay }}>
        {initial ? "✏️ Edit Template" : "✨ New Template"}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: B.textMid, marginBottom: 4, fontFamily: fonts.body }}>
            Title (supports {'{count}'}, {'{category}'}, {'{plural}'})
          </label>
          <input value={title} onChange={e => setTitle(e.target.value)}
            placeholder="e.g. 🚀 {count} New Design{plural} in {category}!"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: B.textMid, marginBottom: 4, fontFamily: fonts.body }}>
            Body (supports {'{count}'}, {'{category}'}, {'{plural}'})
          </label>
          <textarea value={body} onChange={e => setBody(e.target.value)}
            placeholder="e.g. Check out {count} new {category} designs just dropped on Nilam Synthetics."
            rows={3}
            style={{ ...inputStyle, resize: "vertical", minHeight: 70 }}
          />
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
        <Btn onClick={onCancel} variant="ghost" small>Cancel</Btn>
        <Btn onClick={handleSave} disabled={saving || !title.trim()} variant="primary" small>
          {saving ? "Saving..." : initial ? "Save" : "Add Template"}
        </Btn>
      </div>
      {(title || body) && (
        <p style={{ margin: "12px 0 0", fontSize: 12, color: B.textLight, fontFamily: fonts.body, fontStyle: "italic" }}>
          Preview: <strong>{substitutePlaceholders(title || "(title)", 10, "Kurti")}</strong> — {substitutePlaceholders(body || "(body)", 10, "Kurti")}
        </p>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function NotificationsTab({
  showToast,
  notificationTemplates = [],
  categories = [],
  designs = [],
  branding = {},
  onAddTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
}) {
  const { user, profile } = useAuth();

  // Send form state
  const [count, setCount] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [designId, setDesignId] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Template management state
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [templateSaving, setTemplateSaving] = useState(false);

  // Derived
  const selectedCategory = categories.find(c => c.id === categoryId);
  const selectedTemplate = notificationTemplates.find(t => t.id === templateId);

  // Designs filtered by selected category (or all if none selected)
  const filteredDesignsForDropdown = useMemo(() => {
    if (!categoryId) return [];
    return designs.filter(d => d.categoryId === categoryId);
  }, [designs, categoryId]);

  const previewTitle = useMemo(() => {
    if (!selectedTemplate) {
      if (!count) return "";
      return `🚀 ${count} New Design${Number(count) > 1 ? "s" : ""} Added!`;
    }
    return substitutePlaceholders(selectedTemplate.title, count, selectedCategory?.name || categoryId);
  }, [selectedTemplate, count, selectedCategory, categoryId]);

  const previewBody = useMemo(() => {
    if (!selectedTemplate) {
      if (!count) return "";
      return `Check out our latest collection — ${count} new design${Number(count) > 1 ? "s" : ""} just dropped on Nilam Synthetics.`;
    }
    return substitutePlaceholders(selectedTemplate.body, count, selectedCategory?.name || categoryId);
  }, [selectedTemplate, count, selectedCategory, categoryId]);

  // ── Send Handler ──────────────────────────────────────────────────────────
  const handleSend = async () => {
    const num = parseInt(count, 10);
    if (!num || num < 1) {
      showToast?.("Please enter a valid number (1 or more)", B.red);
      return;
    }
    if (num > 500) {
      showToast?.("Maximum 500 new designs at a time", B.red);
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const idToken = await user?.getIdToken();
      const body = {
        count: num,
        triggeredBy: profile?.name || profile?.email || "Admin",
      };
      if (templateId) body.templateId = templateId;
      if (categoryId) body.category = selectedCategory?.name || categoryId;
      const optimizedUrl = withCloudinaryNotificationOptimization(imageUrl.trim());
      if (optimizedUrl) body.imageUrl = optimizedUrl;
      if (selectedCategory) body.categorySlug = selectedCategory.slug || selectedCategory.name.toLowerCase().replace(/\s+/g, "-");

      const res = await fetch(`${PRODUCTS_API_URL}/api/notifications/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Server error (${res.status})`);

      setResult(data);
      showToast?.(`Sent ${data.success}/${data.total} notifications!`, B.green);

      try {
        const { logActivity } = await import("../../../firebase/auth");
        await logActivity(user?.uid, "notification_broadcast",
          `Sent notification using "${selectedTemplate?.title || "default"}" template - ${data.success} delivered`);
      } catch (_) {}

      setCount("");
    } catch (err) {
      showToast?.(err.message, B.red);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !sending) handleSend();
  };

  // ── Template CRUD Handlers ────────────────────────────────────────────────
  const handleSaveTemplate = async (data) => {
    setTemplateSaving(true);
    try {
      if (editTemplate) {
        await onUpdateTemplate(editTemplate.id, data);
        showToast?.("Template updated");
      } else {
        await onAddTemplate(data);
        showToast?.("Template added");
      }
      setShowTemplateForm(false);
      setEditTemplate(null);
    } catch (e) {
      showToast?.(e.message, B.red);
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleDeleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.title || "Untitled"}"?`)) return;
    try {
      await onDeleteTemplate(t.id);
      if (templateId === t.id) setTemplateId("");
      showToast?.("Template deleted", B.red);
    } catch (e) {
      showToast?.(e.message, B.red);
    }
  };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const labelStyle = {
    display: "block", fontSize: 13, fontWeight: 600, color: B.textMid,
    fontFamily: fonts.body, marginBottom: 6,
  };
  const inputStyle = {
    width: "100%", padding: "10px 14px", fontSize: 14, fontWeight: 500,
    fontFamily: fonts.body, border: `2px solid ${B.border}`,
    borderRadius: 8, outline: "none", background: B.cream, color: B.text,
    boxSizing: "border-box", transition: "border-color 0.2s",
  };
  const selectStyle = {
    ...inputStyle, cursor: "pointer", appearance: "auto",
  };

  return (
    <div>
      {/* ── SEND NOTIFICATION FORM ── */}
      <div style={{ maxWidth: 600 }}>
        <h3 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: B.text, fontFamily: fonts.adminDisplay }}>
          Send Push Notification
        </h3>
        <p style={{ margin: "0 0 24px", fontSize: 13, color: B.textLight, fontFamily: fonts.body, lineHeight: 1.6 }}>
          Notify all customers with a dynamic message. Select or create a template,
          choose a category, and optionally add a hero image.
        </p>

        <div style={{ background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 14, padding: "24px 24px 20px" }}>
          {/* Template Selector */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Notification Template</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select value={templateId} onChange={e => setTemplateId(e.target.value)}
                style={{ ...selectStyle, flex: 1 }}
              >
                <option value="">— Default (hardcoded message) —</option>
                {notificationTemplates.map(t => (
                  <option key={t.id} value={t.id}>{t.title || "Untitled"}</option>
                ))}
              </select>
              <button type="button" onClick={() => { setShowTemplateForm(true); setEditTemplate(null); }}
                style={{
                  padding: "10px 14px", fontSize: 13, fontWeight: 700, borderRadius: 8,
                  border: `2px solid ${B.orange}`, background: "#fff", color: B.orange,
                  cursor: "pointer", whiteSpace: "nowrap", fontFamily: fonts.body,
                }}
                title="Add new template"
              >
                + New
              </button>
            </div>
          </div>

          {/* Inline Template Form */}
          {showTemplateForm && (
            <TemplateForm
              initial={editTemplate}
              onSave={handleSaveTemplate}
              onCancel={() => { setShowTemplateForm(false); setEditTemplate(null); }}
              saving={templateSaving}
            />
          )}

          {/* Count */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>New designs count</label>
            <input type="number" min="1" max="500" value={count}
              onChange={(e) => setCount(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="e.g. 10" disabled={sending}
              style={{ ...inputStyle, maxWidth: 160 }}
              onFocus={(e) => (e.target.style.borderColor = B.orange)}
              onBlur={(e) => (e.target.style.borderColor = B.border)}
            />
          </div>

            {/* Category Dropdown */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Category</label>
              <select value={categoryId} onChange={e => { setCategoryId(e.target.value); setDesignId(""); }}
                style={{ ...selectStyle, maxWidth: 300 }}
              >
                <option value="">— None —</option>
                {categories.filter(c => c.active !== false).map(c => (
                  <option key={c.id} value={c.id}>{c.icon || "📁"} {c.name}</option>
                ))}
              </select>
            </div>

            {/* Design Dropdown (only shows if a category is selected) */}
            {categoryId && (
              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Select Design (auto-fills hero image)</label>
                <select value={designId} onChange={e => {
                  const id = e.target.value;
                  setDesignId(id);
                  if (id) {
                    const design = filteredDesignsForDropdown.find(d => d.id === id);
                    if (design) {
                      setImageUrl(design.imageUrl || design.images?.[0] || "");
                    }
                  } else {
                    setImageUrl("");
                  }
                }}
                  style={{ ...selectStyle, maxWidth: 300 }}
                >
                  <option value="">— No design selected —</option>
                  {filteredDesignsForDropdown.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.design_number || d.designNo || "Design"} — ₹{d.price}
                    </option>
                  ))}
                </select>
                {designId && (
                  <div style={{ marginTop: 6, display: "flex", gap: 10, alignItems: "center" }}>
                    {(() => {
                      const d = filteredDesignsForDropdown.find(x => x.id === designId);
                      const src = d?.imageUrl || d?.images?.[0];
                      return src ? (
                        <img src={src} alt="" style={{ width: 48, height: 58, objectFit: "cover", borderRadius: 6, border: `1px solid ${B.borderLight}` }} />
                      ) : null;
                    })()}
                    <span style={{ fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>
                      Image URL auto-populated below. You can edit it manually if needed.
                    </span>
                  </div>
                )}
              </div>
            )}

          {/* Image URL */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Hero Image URL</label>
            <input type="url" value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/hero-image.jpg"
              disabled={sending}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = B.orange)}
              onBlur={(e) => (e.target.style.borderColor = B.border)}
            />
          </div>

          {/* Preview */}
          {(count || templateId) && (
            <div style={{
              background: B.orangePale, border: `1px solid ${B.orangeMid}`,
              borderRadius: 10, padding: "14px 18px", marginBottom: 18,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: B.textMid, marginBottom: 6, fontFamily: fonts.body }}>
                🔍 Notification Preview
              </div>
              <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600, color: B.text, fontFamily: fonts.body }}>
                {previewTitle || "(no title)"}
              </p>
              <p style={{ margin: 0, fontSize: 13, color: B.textMid, fontFamily: fonts.body }}>
                {previewBody || "(no body)"}
              </p>
            </div>
          )}

          {/* Send Button */}
          <button type="button" onClick={handleSend} disabled={sending || !count}
            style={{
              padding: "12px 28px", fontSize: 15, fontWeight: 700, borderRadius: 10,
              border: "none", fontFamily: fonts.body, background: B.orange, color: B.white,
              cursor: sending || !count ? "not-allowed" : "pointer",
              opacity: sending || !count ? 0.6 : 1, transition: "all 0.2s",
            }}
            onMouseEnter={(e) => { if (!sending && count) e.currentTarget.style.background = B.orangeDark; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = B.orange; }}
          >
            {sending ? <span><Spinner size={18} stroke="#fff" /> Sending...</span> : "Send to Everyone"}
          </button>
        </div>

        {/* Result */}
        {result && (
          <div style={{ marginTop: 16, background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 14, padding: "16px 20px" }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: B.text, fontFamily: fonts.body }}>Delivery Summary</p>
            <div style={{ display: "flex", gap: 20, marginTop: 10, fontSize: 13, fontFamily: fonts.body }}>
              <span>Sent: <strong style={{ color: B.green }}>{result.success}</strong></span>
              <span>Failed: <strong style={{ color: result.failed > 0 ? B.red : B.textLight }}>{result.failed}</strong></span>
              <span>Total: <strong>{result.total}</strong></span>
            </div>
          </div>
        )}
      </div>

      {/* ── TEMPLATE MANAGEMENT (list) ── */}
      {notificationTemplates.length > 0 && !showTemplateForm && (
        <div style={{ marginTop: 32, maxWidth: 600 }}>
          <h4 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, color: B.text, fontFamily: fonts.adminDisplay }}>
            Saved Templates ({notificationTemplates.length})
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {notificationTemplates.map(t => (
              <div key={t.id} style={{
                background: B.white, border: `1px solid ${B.borderLight}`,
                borderRadius: 12, padding: "14px 18px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: B.text, fontFamily: fonts.body, marginBottom: 4 }}>{t.title || "Untitled"}</div>
                    <div style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body, lineHeight: 1.5 }}>
                      {(t.body || "").length > 120 ? t.body.slice(0, 120) + "…" : (t.body || "—")}
                    </div>
                    {t.updatedAt && (
                      <div style={{ fontSize: 11, color: B.textLight, marginTop: 6, fontFamily: fonts.body }}>
                        Updated: {new Date(t.updatedAt?.toDate?.() || t.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button type="button" onClick={() => { setEditTemplate(t); setShowTemplateForm(true); }}
                      style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 16, padding: 2 }} title="Edit">
                      ✏️
                    </button>
                    <button type="button" onClick={() => handleDeleteTemplate(t)}
                      style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 16, padding: 2 }} title="Delete">
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, background: B.orangePale, border: `1px solid ${B.orangeMid}`, borderRadius: 10, padding: "14px 18px", fontSize: 12, color: B.textMid, fontFamily: fonts.body, lineHeight: 1.7, maxWidth: 600 }}>
        <strong>How it works:</strong> Select a template, fill in the fields, and send. Placeholders like {'{count}'}, {'{category}'}, and {'{plural}'} are automatically replaced. If no template is selected, the default hardcoded message is used.
      </div>
    </div>
  );
}